import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
} from "@mui/material";
import { Plan } from "../../data/models/Plan/PlanModel";

interface PlansSelectionProps {
  plans: Plan[];
  recommendedCode?: string;
  onSelect?: (plan: Plan) => void;
}

export const PlansSelection: React.FC<PlansSelectionProps> = ({
  plans,
  recommendedCode,
  onSelect,
}) => {
  if (!plans || plans.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No hay planes disponibles en este momento.
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 3,
        bgcolor: "background.default",
        px: 2,
        py: 6,
      }}
    >
      <Typography
        variant="h4"
        fontWeight={800}
        sx={{
          background: "linear-gradient(90deg, #032B66, rgba(24, 59, 255, 1))",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        No tienes ningún plan activo
      </Typography>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ maxWidth: 500, mb: 5 }}
      >
        Elige el plan que mejor se adapte a ti y desbloquea todo el contenido
        exclusivo.
      </Typography>

      <Grid
        container
        spacing={4}
        justifyContent="center"
        sx={{ maxWidth: 1000 }}
      >
        {plans.map((plan) => {
          const isRecommended = plan.code === recommendedCode;

          return (
            <Grid item xs={12} sm={6} md={4} key={plan.code}>
              <Card
                sx={{
                  borderRadius: 4,
                  position: "relative",
                  boxShadow: isRecommended
                    ? "0 12px 30px rgba(51, 78, 234, 0.15)"
                    : "0 8px 20px rgba(0,0,0,0.06)",
                  background: isRecommended
                    ? "linear-gradient(180deg, #f5f9ffff 0%, #ffffff 100%)"
                    : "linear-gradient(180deg, #ffffff 0%, #f9fafc 100%)",
                  border: isRecommended
                    ? "2px solid rgba(51, 94, 234, 0.25)"
                    : "1px solid rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: isRecommended
                      ? "0 18px 40px rgba(51, 94, 234, 0.25)"
                      : "0 12px 28px rgba(0,0,0,0.08)",
                  },
                }}
              >
                {isRecommended && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 8,
                      px: 1.5,
                      py: 0.3,
                      borderRadius: 999,
                      bgcolor: "primary.main",
                      color: "white",
                      fontSize: 8,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    Recomendado
                  </Box>
                )}

                <CardHeader
                  title={
                    <Typography
                      variant="h6"
                      fontWeight={800}
                      sx={{
                        color: isRecommended ? "primary.main" : "text.primary",
                      }}
                    >
                      {plan.name}
                    </Typography>
                  }
                  subheader={
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      color={isRecommended ? "secondary.main" : "primary.main"}
                    >
                      ${plan.priceUsd.toLocaleString("en-US")} USD
                    </Typography>
                  }
                  sx={{ textAlign: "center", pb: 0 }}
                />

                <CardContent sx={{ textAlign: "center" }}>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 3,
                      color: "text.secondary",
                      minHeight: 48,
                    }}
                  >
                    {plan.description ||
                      "Acceso a recursos exclusivos y materiales personalizados."}
                  </Typography>

                  <Button
                    variant="contained"
                    fullWidth
                    disableElevation
                    sx={{
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: "none",
                      py: 1.3,
                      background: isRecommended
                        ? "linear-gradient(90deg, #1D4ED8 0%, #3B82F6 100%)"
                        : "linear-gradient(90deg, #183BFF 0%, #0061FF 100%)",

                      "&:hover": {
                        background: isRecommended
                          ? "linear-gradient(90deg, #1D4ED8 0%, #3B82F6 100%)"
                          : "linear-gradient(90deg, #183BFF 0%, #0061FF 100%)",
                      },
                    }}
                    onClick={() => onSelect?.(plan)}
                  >
                    {isRecommended ? "Comprar" : "Comprar"}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
